import { useEffect } from "react";
import { NavLink, Navigate, Outlet, useNavigate } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { Bike, Home, LogOut } from "lucide-react";
import { cn } from "@tarawa/ui";
import { api } from "@/lib/api";
import { useDriver } from "@/lib/driver";
import { Loading } from "./ui";

const NAV = [
  { to: "/", label: "Accueil", icon: Home, end: true },
  { to: "/mes-commandes", label: "Mes commandes", icon: Bike },
];

/** Protected Driver Dashboard shell (UX guard — every action is enforced by the API). */
export function Layout() {
  const { driver, isLoading, setDriver } = useDriver();
  const qc = useQueryClient();
  const navigate = useNavigate();

  useEffect(() => {
    const onUnauthorized = () => {
      setDriver(null);
      qc.clear();
    };
    window.addEventListener("tw-driver-unauthorized", onUnauthorized);
    return () => window.removeEventListener("tw-driver-unauthorized", onUnauthorized);
  }, [qc, setDriver]);

  if (isLoading) return <Loading />;
  if (!driver) return <Navigate to="/login" replace />;

  const logout = async () => {
    await api("/auth/logout", { method: "POST" }).catch(() => undefined);
    setDriver(null);
    qc.clear();
    navigate("/login");
  };

  return (
    <div className="min-h-dvh pb-24 md:pb-10">
      <header className="sticky top-0 z-30 border-b border-line/70 bg-surface/95 backdrop-blur">
        <div className="mx-auto flex h-16 max-w-4xl items-center gap-3 px-4">
          <img src="/favicon.svg" alt="" className="size-9" />
          <div className="min-w-0 flex-1">
            <p className="leading-none font-extrabold text-brand-800">Tarawa Livreur</p>
            <p className="truncate text-xs text-muted">{driver.fullName}</p>
          </div>
          <nav className="hidden items-center gap-1 md:flex">
            {NAV.map(({ to, label, icon: Icon, end }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                className={({ isActive }) =>
                  cn("flex h-10 items-center gap-2 rounded-full px-4 text-sm font-semibold", isActive ? "bg-brand-50 text-brand-700" : "text-muted hover:text-ink")
                }
              >
                <Icon className="size-4" />
                {label}
              </NavLink>
            ))}
          </nav>
          <button onClick={logout} className="flex size-10 items-center justify-center rounded-full text-terracotta-600 hover:bg-terracotta-50" aria-label="Se déconnecter">
            <LogOut className="size-5" />
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-4 pt-5">
        <Outlet />
      </main>

      <nav className="fixed inset-x-0 bottom-0 z-30 border-t border-line/70 bg-surface/95 pb-[env(safe-area-inset-bottom)] backdrop-blur md:hidden" aria-label="Navigation">
        <ul className="grid grid-cols-2">
          {NAV.map(({ to, label, icon: Icon, end }) => (
            <li key={to}>
              <NavLink
                to={to}
                end={end}
                className={({ isActive }) => cn("flex h-16 flex-col items-center justify-center gap-1 text-xs font-semibold", isActive ? "text-brand-700" : "text-subtle")}
              >
                <Icon className="size-[22px]" />
                {label}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>
    </div>
  );
}
